import type { DocumentRecord, DocumentType, RequestType } from '@bcl/shared';
import { getQualificationConfig } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Circle, Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { extractErrorMessage } from '@/features/auth/error-message';
import {
  deleteDocument,
  getDownloadUrl,
  listDocumentsForProject,
  requestUploadUrl,
  uploadToPresignedUrl,
} from './documents-api';

const documentTypeLabels: Record<DocumentType, string> = {
  piece_identite: "Pièce d'identité",
  permis_construire: 'Permis de construire',
  attestation_adressage: "Attestation d'adressage",
  plans_architecte: 'Plans architecte',
  photo_interieur: 'Photos intérieur',
  photo_exterieur: 'Photos extérieur',
  photo_installation: "Photos d'installation",
  plan_masse: 'Plan de masse',
  plans_vrd: 'Plans VRD',
  courrier_operateur: 'Courrier opérateur',
  autre: 'Autre',
};

const documentStatusLabels = {
  en_attente: 'En attente',
  valide: 'Validé',
  refuse: 'Refusé',
  a_completer: 'À compléter',
};

/**
 * Fichier en attente de téléversement : le user a drag-and-drop ou
 * sélectionné un fichier, on l'a ajouté à la liste, on lui propose de
 * renommer (clic sur le titre) et de choisir le type avant d'envoyer.
 */
type StagedFile = {
  id: string;
  file: File;
  displayName: string;
  documentType: DocumentType;
};

function genId(): string {
  return `staged-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
}

export function DocumentsSection({
  projectId,
  requestType,
  onBusyChange,
}: {
  projectId: string;
  /**
   * Type de demande qualifie, utilise pour afficher la checklist des
   * documents requis. Optionnel : si absent, on n'affiche pas la checklist.
   */
  requestType?: RequestType;
  /**
   * Notifie le parent (wizard) qu'un upload est en cours OU que des fichiers
   * sont déposés mais pas encore téléversés. Permet de verrouiller la
   * navigation (« Passer au paiement ») tant que ce n'est pas terminé, pour
   * éviter d'avancer sur un dossier incomplet.
   */
  onBusyChange?: (busy: boolean) => void;
}) {
  const qc = useQueryClient();
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Progression d'upload par fichier (0→100), pour un feedback visuel direct.
  const [progress, setProgress] = useState<Record<string, number>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const list = useQuery({
    queryKey: ['documents', projectId],
    queryFn: () => listDocumentsForProject(projectId),
  });

  const uploadMut = useMutation({
    mutationFn: async (entry: StagedFile) => {
      // Affiche la barre dès le clic (phase de préparation incluse) pour que
      // l'utilisateur voie tout de suite qu'il se passe quelque chose.
      setProgress((m) => ({ ...m, [entry.id]: 0 }));
      const renamed =
        entry.displayName.trim() && entry.displayName !== entry.file.name
          ? new File([entry.file], entry.displayName, { type: entry.file.type })
          : entry.file;
      const { uploadUrl, document } = await requestUploadUrl({
        projectId,
        documentType: entry.documentType,
        filename: renamed.name,
        contentType: renamed.type || 'application/octet-stream',
        sizeBytes: renamed.size,
      });
      await uploadToPresignedUrl(uploadUrl, renamed, (p) =>
        setProgress((m) => ({ ...m, [entry.id]: p })),
      );
      return document;
    },
    onSuccess: (_doc, entry) => {
      // Si un upload precedent avait laisse un message d'erreur a l'ecran
      // (typiquement un 400 retry-passable de S3), on l'efface des qu'un
      // upload reussit. L'utilisateur ne doit pas voir « Upload echoue »
      // alors que le fichier finit bien dans la liste.
      setError(null);
      setStaged((prev) => prev.filter((s) => s.id !== entry.id));
      qc.invalidateQueries({ queryKey: ['documents', projectId] });
    },
    onError: (e) => setError(extractErrorMessage(e)),
    onSettled: (_d, _e, entry) =>
      setProgress((m) => {
        const next = { ...m };
        delete next[entry.id];
        return next;
      }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteDocument(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', projectId] }),
  });

  function addFiles(files: FileList | File[] | null) {
    if (!files) return;
    const list = Array.from(files);
    if (list.length === 0) return;
    setError(null);
    setStaged((prev) => [
      ...prev,
      ...list.map((f) => ({
        id: genId(),
        file: f,
        displayName: f.name,
        documentType: 'autre' as DocumentType,
      })),
    ]);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  }

  async function openDownload(doc: DocumentRecord) {
    const url = await getDownloadUrl(doc.id);
    window.open(url, '_blank');
  }

  function updateStaged(id: string, patch: Partial<Omit<StagedFile, 'id' | 'file'>>) {
    setStaged((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...patch } : s)),
    );
  }

  function removeStaged(id: string) {
    setStaged((prev) => prev.filter((s) => s.id !== id));
  }

  async function uploadAll() {
    setError(null);
    for (const entry of staged) {
      await uploadMut.mutateAsync(entry);
    }
  }

  const requiredTypes: DocumentType[] = requestType
    ? getQualificationConfig(requestType).requiredDocuments
    : [];
  const uploadedByType = new Map<DocumentType, number>();
  for (const d of list.data ?? []) {
    uploadedByType.set(d.documentType, (uploadedByType.get(d.documentType) ?? 0) + 1);
  }

  // Occupé tant qu'un upload tourne OU que des fichiers déposés n'ont pas été
  // téléversés. On remonte l'info au parent pour bloquer « Passer au paiement ».
  const busy = uploadMut.isPending || staged.length > 0;
  useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Documents</CardTitle>
        <CardDescription>
          Déposez vos pièces justificatives. BCL Telecom les valide ensuite.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {requiredTypes.length > 0 && (
          <div
            className="space-y-2 rounded-md border p-3"
            data-testid="documents-checklist"
          >
            <p className="text-sm font-medium">Documents requis pour votre pack</p>
            <ul className="space-y-1 text-sm">
              {requiredTypes.map((t) => {
                const count = uploadedByType.get(t) ?? 0;
                const done = count > 0;
                return (
                  <li
                    key={t}
                    className="flex items-center gap-2"
                    data-required-type={t}
                    data-required-done={done ? 'true' : 'false'}
                  >
                    {done ? (
                      <Check
                        className="h-4 w-4 text-emerald-600"
                        aria-hidden="true"
                      />
                    ) : (
                      <Circle
                        className="h-4 w-4 text-gray-400"
                        aria-hidden="true"
                      />
                    )}
                    <span
                      className={
                        done ? 'text-emerald-700' : 'text-gray-600'
                      }
                    >
                      {documentTypeLabels[t]}
                      {done
                        ? ` — Déposé (${count} fichier${count > 1 ? 's' : ''})`
                        : ' — À déposer'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Drop zone */}
        <div
          role="presentation"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
          className={[
            'flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 text-center text-sm cursor-pointer transition-colors',
            isDragging
              ? 'border-bcl-blue bg-bcl-blue/5 text-bcl-blue'
              : 'border-gray-300 bg-gray-50 text-gray-600 hover:border-bcl-blue hover:bg-bcl-blue/5',
          ].join(' ')}
        >
          <Upload className="h-6 w-6" aria-hidden="true" />
          <p className="font-medium">
            Glissez-déposez vos fichiers ici ou cliquez pour parcourir
          </p>
          <p className="text-xs text-muted-foreground">
            PDF, JPG, PNG · 25 Mo max par fichier
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            data-testid="documents-file-input"
            onChange={(e) => {
              addFiles(e.target.files);
              if (fileInputRef.current) fileInputRef.current.value = '';
            }}
          />
        </div>

        {/* Liste des fichiers déposés en attente de téléversement */}
        {staged.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">
              Fichiers prêts à téléverser ({staged.length})
            </p>
            <ul className="space-y-2 text-sm">
              {staged.map((s) => (
                <li
                  key={s.id}
                  className="flex flex-wrap items-center gap-2 rounded-md border p-2"
                >
                  <div className="min-w-[180px] flex-1">
                    {editingId === s.id ? (
                      <Input
                        autoFocus
                        value={s.displayName}
                        onChange={(e) =>
                          updateStaged(s.id, { displayName: e.target.value })
                        }
                        onBlur={() => setEditingId(null)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === 'Escape') {
                            setEditingId(null);
                          }
                        }}
                        aria-label="Renommer le fichier"
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditingId(s.id)}
                        className="text-left font-medium hover:underline"
                        title="Cliquez pour renommer"
                      >
                        {s.displayName}
                      </button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      {(s.file.size / 1024).toFixed(0)} ko
                    </p>
                  </div>
                  <select
                    value={s.documentType}
                    onChange={(e) =>
                      updateStaged(s.id, {
                        documentType: e.target.value as DocumentType,
                      })
                    }
                    aria-label="Type de document"
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    {(Object.keys(documentTypeLabels) as DocumentType[]).map(
                      (k) => (
                        <option key={k} value={k}>
                          {documentTypeLabels[k]}
                        </option>
                      ),
                    )}
                  </select>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeStaged(s.id)}
                    aria-label={`Retirer ${s.displayName}`}
                    disabled={progress[s.id] !== undefined}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  {progress[s.id] !== undefined && (
                    <div className="flex w-full items-center gap-2">
                      <Progress value={progress[s.id]} className="h-2 flex-1" />
                      <span className="w-24 text-right text-xs tabular-nums text-bcl-blue">
                        {progress[s.id] === 0
                          ? 'Préparation…'
                          : `Envoi ${progress[s.id]}%`}
                      </span>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <Button
                onClick={uploadAll}
                disabled={uploadMut.isPending}
              >
                <Upload className="mr-2 h-4 w-4" />
                {uploadMut.isPending
                  ? 'Téléversement…'
                  : `Téléverser ${staged.length} fichier${staged.length > 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}

        {/* Documents déjà téléversés */}
        {list.data && list.data.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Documents déjà déposés</p>
            <ul className="space-y-2 text-sm">
              {list.data.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between rounded-md border p-2"
                >
                  <div>
                    <p className="font-medium">{d.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {documentTypeLabels[d.documentType]} ·{' '}
                      {documentStatusLabels[d.status]}
                      {d.reviewReason ? ` — ${d.reviewReason}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDownload(d)}
                    >
                      Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteMut.mutate(d.id)}
                    >
                      Supprimer
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
